// src/User/MisCertificados.js — JCUMAS 060.013
// Formulario para solicitar un certificado + listado del estado de las
// solicitudes de certificado ya presentadas por el usuario.
import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Spinner,
  Badge,
  Form,
  Button,
  Container,
  Alert,
} from "react-bootstrap";
import Header from "../Components/Header";
import "bootstrap-icons/font/bootstrap-icons.css";

const BASE_URL = process.env.REACT_APP_API_URL;

// Mismo mapa que Admin/GestionCertificados.js. Pensado para añadir más tipos
// sin tocar el esquema de datos (ver DatosCertificado más abajo para los que
// necesiten campos propios).
// JCUMAS 060.013: pendiente de definir. Añadir aquí cada tipo cuando se
// den de alta (y su equivalente en CERTIFICADOS_DISPONIBLES del backend):
// const TIPOS_CERTIFICADO = { clave_tipo: "Nombre visible" };
const TIPOS_CERTIFICADO = {};

const PARCELA_VACIA = {
  provincia: "",
  municipio: "",
  poligono: "",
  parcela: "",
  recinto: "",
  superficie_total: "",
  superficie_riego: "",
  dotacion: "",
};

const MisCertificados = () => {
  const [certificados, setCertificados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tipoSeleccionado, setTipoSeleccionado] = useState("");
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [descargando, setDescargando] = useState(null);

  // Campos propios de"Certificado de la Comunidad de Regantes". Los datos
  // de la Comunidad (nombre, cabecera, firma…) no se piden aquí: se generan
  // en el backend igual que en el certificado de estar al corriente de pago.
  const [representacionActiva, setRepresentacionActiva] = useState(false);
  const [entidadNombre, setEntidadNombre] = useState("");
  const [entidadNif, setEntidadNif] = useState("");
  const [miembroDesde, setMiembroDesde] = useState("");
  const [sigpacAnio, setSigpacAnio] = useState("");
  const [parcelas, setParcelas] = useState([{ ...PARCELA_VACIA }]);

  const userId = localStorage.getItem("id");
  const token = localStorage.getItem("token");

  const fetchCertificados = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/solicitudes/usuarios/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCertificados(data.filter((s) => s.TipoSolicitud === "certificado"));
      } else {
        setCertificados([]);
      }
    } catch (e) {
      console.error("Error al obtener los certificados:", e);
    } finally {
      setLoading(false);
    }
  };

  // Al entrar aquí, el usuario reconoce el estado actual de sus certificados
  // (esto es lo que hace desaparecer el badge de"nuevo" en el menú y en las
  // filas la próxima vez que visite esta pantalla).
  const marcarVistos = async () => {
    try {
      await fetch(`${BASE_URL}/solicitudes/certificados/marcar-vistos`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) {
      console.error("Error al marcar certificados como vistos:", e);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchCertificados();
      marcarVistos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const actualizarParcela = (index, campo, valor) => {
    setParcelas((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [campo]: valor } : p)),
    );
  };

  const añadirParcela = () => {
    setParcelas((prev) => [...prev, { ...PARCELA_VACIA }]);
  };

  const eliminarParcela = (index) => {
    setParcelas((prev) =>
      prev.length > 1 ? prev.filter((_, i) => i !== index) : prev,
    );
  };

  const resetFormularioComunidadRegantes = () => {
    setRepresentacionActiva(false);
    setEntidadNombre("");
    setEntidadNif("");
    setMiembroDesde("");
    setSigpacAnio("");
    setParcelas([{ ...PARCELA_VACIA }]);
  };

  const solicitarCertificado = async (e) => {
    e.preventDefault();
    setEnviando(true);
    try {
      const now = new Date();
      // FechaSolicitud es una columna DATE en el backend: solo necesita la
      // fecha (YYYY-MM-DD). El .join("") de antes pegaba la fecha y la hora
      // sin separador ("2026-09-2218:35:35.313"), algo que MySQL rechaza
      // con "Incorrect date value".
      const fechaHora = now.toISOString().split("T")[0];

      let datosCertificado = null;
      if (tipoSeleccionado === "comunidad_regantes") {
        datosCertificado = {
          representacion: {
            activa: representacionActiva,
            entidad_nombre: representacionActiva ? entidadNombre : "",
            entidad_nif: representacionActiva ? entidadNif : "",
          },
          miembro_desde: miembroDesde,
          sigpac_anio: sigpacAnio,
          parcelas: parcelas.filter((p) =>
            Object.values(p).some((v) => (v || "").trim() !== ""),
          ),
        };
      }

      const res = await fetch(`${BASE_URL}/solicitudes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          UsuarioID: userId,
          Estado: "pendiente",
          TipoSolicitud: "certificado",
          TipoCertificado: tipoSeleccionado,
          Descripcion: motivo,
          FechaSolicitud: fechaHora,
          DatosCertificado: datosCertificado,
        }),
      });
      if (res.ok) {
        setMotivo("");
        resetFormularioComunidadRegantes();
        alert("Su solicitud de certificado ha sido registrada.");
        fetchCertificados();
      } else {
        alert("Error al registrar la solicitud de certificado.");
      }
    } catch (e) {
      alert("No se pudo conectar con el servidor.");
    } finally {
      setEnviando(false);
    }
  };

  const descargar = async (documentoId, nombre) => {
    setDescargando(documentoId);
    try {
      const res = await fetch(
        `${BASE_URL}/documentos/${documentoId}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = nombre || "certificado.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      alert("No se pudo descargar el certificado.");
    } finally {
      setDescargando(null);
    }
  };

  const renderEstado = (cert) => {
    if (cert.FirmadoAdmin) return <Badge bg="success">Enviado</Badge>;
    if (cert.DocumentoID)
      return (
        <Badge bg="warning" text="dark">
          En preparación
        </Badge>
      );
    return <Badge bg="secondary">Solicitado</Badge>;
  };

  return (
    <div className="d-flex flex-column">
      <Header />
      <main className="flex-grow-1">
        <Container fluid className="py-3 px-4">
          <h4 className="section-title mb-2">
            <i className="bi bi-patch-check text-primary me-2"></i>
            Mis Certificados
          </h4>
          <p className="text-muted mb-4">
            Solicite un certificado y consulte el estado de sus solicitudes. Una
            vez firmado por la administración, podrá descargarlo aquí.
          </p>

          <Card
            className="shadow-sm mb-4 overflow-hidden"
            style={{ borderRadius: "0.75rem" }}
          >
            <Card.Header className="content-card-header py-3 px-4">
              <i className="bi bi-file-earmark-plus me-2"></i>
              Solicitar un certificado
            </Card.Header>
            <Card.Body className="p-4">
              {Object.keys(TIPOS_CERTIFICADO).length === 0 ? (
                <Alert variant="secondary" className="mb-0">
                  <i className="bi bi-hourglass-split me-2"></i>
                  Todavía no hay tipos de certificado disponibles. En cuanto se
                  habiliten, podrá solicitarlos desde aquí.
                </Alert>
              ) : (
              <Form onSubmit={solicitarCertificado}>
                <Form.Group
                  className="mb-4"
                  controlId="tipoCertificado"
                  style={{ maxWidth: 500 }}
                >
                  <Form.Label>Tipo de certificado</Form.Label>
                  <Form.Select
                    value={tipoSeleccionado}
                    onChange={(e) => setTipoSeleccionado(e.target.value)}
                    required
                  >
                    <option value="" disabled>
                      Selecciona un certificado
                    </option>
                    {Object.entries(TIPOS_CERTIFICADO).map(
                      ([valor, etiqueta]) => (
                        <option key={valor} value={valor}>
                          {etiqueta}
                        </option>
                      ),
                    )}
                  </Form.Select>
                </Form.Group>

                {tipoSeleccionado === "comunidad_regantes" && (
                  <div
                    className="rounded-3 p-4 mb-4"
                    style={{
                      backgroundColor: "var(--sp-primary-soft)",
                      border: "1px solid var(--sp-primary-soft-border)",
                    }}
                  >
                    <h6 className="mb-3 text-primary">
                      <i className="bi bi-info-circle me-2"></i>
                      Datos para el certificado
                    </h6>

                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        id="representacionActiva"
                        label="Actúo como representante de una entidad (no en mi propio nombre)"
                        checked={representacionActiva}
                        onChange={(e) =>
                          setRepresentacionActiva(e.target.checked)
                        }
                      />
                    </Form.Group>

                    {representacionActiva && (
                      <div className="row mb-3 gy-3">
                        <div className="col-md-6">
                          <Form.Label>Nombre de la entidad</Form.Label>
                          <Form.Control
                            value={entidadNombre}
                            onChange={(e) => setEntidadNombre(e.target.value)}
                            required={representacionActiva}
                          />
                        </div>
                        <div className="col-md-6">
                          <Form.Label>NIF de la entidad</Form.Label>
                          <Form.Control
                            value={entidadNif}
                            onChange={(e) => setEntidadNif(e.target.value)}
                            required={representacionActiva}
                          />
                        </div>
                      </div>
                    )}

                    <div className="row mb-4 gy-3">
                      <div className="col-md-6">
                        <Form.Label>Miembro de la Comunidad desde</Form.Label>
                        <Form.Control
                          type="date"
                          value={miembroDesde}
                          onChange={(e) => setMiembroDesde(e.target.value)}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <Form.Label>Año de publicación SIGPAC</Form.Label>
                        <Form.Control
                          type="number"
                          placeholder="Ej. 2024"
                          value={sigpacAnio}
                          onChange={(e) => setSigpacAnio(e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    <Form.Label className="d-block mb-2">
                      Recintos SIGPAC con aprovechamiento de riego
                    </Form.Label>
                    <div
                      className="table-responsive rounded-3 overflow-hidden border mb-2"
                      style={{ backgroundColor: "#fff" }}
                    >
                      <Table
                        bordered
                        size="sm"
                        className="align-middle mb-0 bg-white"
                        style={{ minWidth: 900 }}
                      >
                        <thead className="thead-soft thead-label">
                          <tr>
                            <th className="py-2">Provincia</th>
                            <th className="py-2">Municipio</th>
                            <th className="py-2">Polígono</th>
                            <th className="py-2">Parcela</th>
                            <th className="py-2">Recinto</th>
                            <th className="py-2">Sup. total (ha)</th>
                            <th className="py-2">Sup. riego (ha)</th>
                            <th className="py-2">Dotación (hm³/ha)</th>
                            <th className="py-2" style={{ width: 44 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {parcelas.map((p, i) => (
                            <tr key={i}>
                              <td className="py-2">
                                <Form.Control
                                  size="sm"
                                  value={p.provincia}
                                  onChange={(e) =>
                                    actualizarParcela(
                                      i,
                                      "provincia",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td className="py-2">
                                <Form.Control
                                  size="sm"
                                  value={p.municipio}
                                  onChange={(e) =>
                                    actualizarParcela(
                                      i,
                                      "municipio",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td className="py-2">
                                <Form.Control
                                  size="sm"
                                  value={p.poligono}
                                  onChange={(e) =>
                                    actualizarParcela(
                                      i,
                                      "poligono",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td className="py-2">
                                <Form.Control
                                  size="sm"
                                  value={p.parcela}
                                  onChange={(e) =>
                                    actualizarParcela(
                                      i,
                                      "parcela",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td className="py-2">
                                <Form.Control
                                  size="sm"
                                  value={p.recinto}
                                  onChange={(e) =>
                                    actualizarParcela(
                                      i,
                                      "recinto",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td className="py-2">
                                <Form.Control
                                  size="sm"
                                  value={p.superficie_total}
                                  onChange={(e) =>
                                    actualizarParcela(
                                      i,
                                      "superficie_total",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td className="py-2">
                                <Form.Control
                                  size="sm"
                                  value={p.superficie_riego}
                                  onChange={(e) =>
                                    actualizarParcela(
                                      i,
                                      "superficie_riego",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td className="py-2">
                                <Form.Control
                                  size="sm"
                                  value={p.dotacion}
                                  onChange={(e) =>
                                    actualizarParcela(
                                      i,
                                      "dotacion",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td className="py-2 text-center">
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="text-danger p-0"
                                  onClick={() => eliminarParcela(i)}
                                  disabled={parcelas.length === 1}
                                  title="Eliminar recinto"
                                >
                                  <i className="bi bi-trash fs-6"></i>
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={añadirParcela}
                      className="px-3"
                    >
                      <i className="bi bi-plus-lg me-1"></i>Añadir recinto
                    </Button>
                    <p className="text-muted small mt-2 mb-0">
                      Incluya todos los recintos SIGPAC para los que solicite el
                      certificado. La superficie debe ir reflejada en ha.
                    </p>
                  </div>
                )}

                <Form.Group
                  className="mb-4"
                  controlId="motivoCertificado"
                  style={{ maxWidth: 500 }}
                >
                  <Form.Label>Observaciones (opcional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Indique cualquier detalle adicional para su solicitud…"
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                  />
                </Form.Group>
                <Button
                  type="submit"
                  variant="primary"
                  className="px-4 py-2"
                  disabled={enviando || !tipoSeleccionado}
                >
                  {enviando ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Enviando…
                    </>
                  ) : (
                    <>
                      <i className="bi bi-send me-2"></i>Solicitar certificado
                    </>
                  )}
                </Button>
              </Form>
              )}
            </Card.Body>
          </Card>

          <Card
            className="shadow-sm overflow-hidden mb-4"
            style={{ borderRadius: "0.75rem" }}
          >
            <Card.Header className="content-card-header py-3 px-4">
              <i className="bi bi-list-check me-2"></i>
              Solicitudes de certificado
            </Card.Header>

            {loading ? (
              <Card.Body className="text-center py-5">
                <Spinner animation="border" />
                <p className="mt-3">Cargando…</p>
              </Card.Body>
            ) : certificados.length === 0 ? (
              <Card.Body>
                <p className="text-muted text-center py-4 mb-0">
                  Todavía no ha solicitado ningún certificado.
                </p>
              </Card.Body>
            ) : (
              <div
                className="table-responsive"
                style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
              >
                <Table hover className="align-middle mb-0">
                  <thead className="thead-brand thead-label">
                    <tr>
                      <th className="py-3 ps-4">Nº Registro</th>
                      <th className="py-3">Tipo</th>
                      <th className="py-3">Fecha de solicitud</th>
                      <th className="py-3">Estado</th>
                      <th className="py-3 pe-4">Descarga</th>
                    </tr>
                  </thead>
                  <tbody>
                    {certificados.map((cert) => (
                      <tr key={cert.SolicitudID}>
                        <td className="ps-4">
                          <code className="text-primary fw-bold">
                            {cert.NumeroRegistro || "—"}
                          </code>
                          {!cert.VistoUsuario && (
                            <span className="badge bg-danger rounded-pill ms-2">
                              Nuevo
                            </span>
                          )}
                        </td>
                        <td className="small">
                          {TIPOS_CERTIFICADO[cert.TipoCertificado] ||
                            cert.TipoCertificado}
                        </td>
                        <td>
                          <small>
                            {cert.FechaSolicitud
                              ? new Date(
                                  cert.FechaSolicitud,
                                ).toLocaleDateString("es-ES", {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                })
                              : "—"}
                          </small>
                        </td>
                        <td>{renderEstado(cert)}</td>
                        <td className="pe-4">
                          {cert.FirmadoAdmin ? (
                            <Button
                              size="sm"
                              variant="outline-success"
                              onClick={() =>
                                descargar(
                                  cert.DocumentoID,
                                  cert.NombreDocumento,
                                )
                              }
                              disabled={descargando === cert.DocumentoID}
                            >
                              <i className="bi bi-download me-1"></i>Descargar
                            </Button>
                          ) : (
                            <span className="text-muted small">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card>
        </Container>
      </main>
    </div>
  );
};

export default MisCertificados;
